// Types
import type { Ref, ComputedRef } from 'vue';
import type { TPagination, TPaginated } from '~/misc/resource/types';
import type { TOutboxEmail } from '~/modules/opportunities/automated-email/types';
import type { TAutomatedEmailOutboxFilters } from '~/modules/opportunities/automated-email/outbox/composables/use-outbox-filters/types';
import type {
  TAutomatedEmailOutboxListQuery,
  TFetchOutboxOptions,
  TOutboxSendEntry,
  TOutboxSendPayload,
} from '~/modules/opportunities/automated-email/outbox/types';

// Models
import { OutboxModel } from '~/modules/opportunities/automated-email/outbox/models';

// Composables
import { useNotifyResource } from '~/misc/resource/composables/use-notify-resource';

export const useOutbox = (filters: TAutomatedEmailOutboxFilters) => {
  const model: OutboxModel = new OutboxModel();
  const { notifySuccess, notifyFailure } = useNotifyResource();

  const emails: Ref<TOutboxEmail[]> = ref<TOutboxEmail[]>([]);
  const pagination: Ref<TPagination | null> = ref<TPagination | null>(null);

  const selectedIds: Ref<Set<number>> = ref<Set<number>>(new Set());
  const isLoading: Ref<boolean> = ref<boolean>(false);
  const isSending: Ref<boolean> = ref<boolean>(false);
  const isDismissing: Ref<boolean> = ref<boolean>(false);

  const hasEmails: ComputedRef<boolean> = computed<boolean>(() => emails.value.length > 0);

  const selectedCount: ComputedRef<number> = computed<number>(() => selectedIds.value.size);

  const hasSelection: ComputedRef<boolean> = computed<boolean>(() => selectedIds.value.size > 0);

  const allSelected: ComputedRef<boolean> = computed<boolean>(
    () => emails.value.length > 0 && selectedIds.value.size === emails.value.length
  );

  const setPagination = (value: TPagination | null): void => {
    pagination.value = value;
  };

  const setIsLoading = (value: boolean): void => {
    isLoading.value = value;
  };

  const setIsSending = (value: boolean): void => {
    isSending.value = value;
  };

  const setIsDismissing = (value: boolean): void => {
    isDismissing.value = value;
  };

  const setEmails = (value: TOutboxEmail[]): void => {
    emails.value = value;
  };

  const setSelectedIds = (value: Set<number> | number[]): void => {
    selectedIds.value = value instanceof Set ? value : new Set(value);
  };

  const getSelectedIds = (): Set<number> => selectedIds.value;

  const isSelected = (id: number): boolean => getSelectedIds().has(id);

  const toggleSelection = (id: number): void => {
    const next: Set<number> = new Set<number>(getSelectedIds());

    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }

    setSelectedIds(next);
  };

  const toggleSelectAll = (): void => {
    if (allSelected.value) {
      setSelectedIds(new Set<number>());
    } else {
      setSelectedIds(new Set<number>(emails.value.map((e: TOutboxEmail): number => e.id)));
    }
  };

  const clearSelection = (): void => {
    setSelectedIds(new Set<number>());
  };

  const pruneSelection = (ids: number[]): void => {
    const idSet: Set<number> = new Set<number>(ids);
    const next: Set<number> = new Set<number>();

    getSelectedIds().forEach((id: number): void => {
      if (!idSet.has(id)) {
        next.add(id);
      }
    });

    setSelectedIds(next);
  };

  const currentOutboxPage = (): number => pagination.value?.current_page ?? 1;

  const fetchOutbox = async (
    page: number = 1,
    options?: TFetchOutboxOptions
  ): Promise<void> => {
    const silent: boolean = options?.silent === true;

    if (!silent) {
      setIsLoading(true);
    }

    try {
      const query: TAutomatedEmailOutboxListQuery | null = filters.toQueryParams(page);

      if (query === null) {
        setEmails([]);
        setPagination(null);

        return;
      }

      const { data, meta }: TPaginated<TOutboxEmail> = await model.getOutboxPage(query);

      setEmails(data);
      setPagination(meta);
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  };

  const toSendPayload = (entry: TOutboxSendEntry): TOutboxSendPayload | undefined => {
    const payload: TOutboxSendPayload = {};

    if (entry.subject !== undefined) {
      payload.subject = entry.subject;
    }

    if (entry.content !== undefined) {
      payload.content = entry.content;
    }

    return payload.subject !== undefined || payload.content !== undefined ? payload : undefined;
  };

  const sendEmails = async (entries: TOutboxSendEntry[]): Promise<void> => {
    if (entries.length === 0) {
      return;
    }

    setIsSending(true);

    try {
      await entries.reduce(
        (chain: Promise<void>, entry: TOutboxSendEntry): Promise<void> =>
          chain.then((): Promise<void> => model.sendOutboxEntry(entry.id, toSendPayload(entry))),
        Promise.resolve()
      );

      notifySuccess();
      pruneSelection(entries.map((entry: TOutboxSendEntry): number => entry.id));
      await fetchOutbox(currentOutboxPage(), { silent: true });
    } catch {
      notifyFailure('There was an issue sending this email, please try again later.');
      fetchOutbox(currentOutboxPage(), { silent: true }).catch(() => undefined);
    } finally {
      setIsSending(false);
    }
  };

  const dismissEmails = async (ids: number[]): Promise<void> => {
    if (ids.length === 0) {
      return;
    }

    setIsDismissing(true);

    try {
      await Promise.all(ids.map((id: number) => model.dismissOutboxEntry(id)));

      notifySuccess();
      pruneSelection(ids);
      await fetchOutbox(currentOutboxPage(), { silent: true });
    } catch {
      notifyFailure();
      fetchOutbox(currentOutboxPage(), { silent: true }).catch(() => undefined);
    } finally {
      setIsDismissing(false);
    }
  };

  const sendSelected = async (): Promise<void> => {
    const entries: TOutboxSendEntry[] = [...getSelectedIds()].map(
      (id: number): TOutboxSendEntry => ({ id })
    );

    if (entries.length === 0) {
      return;
    }

    await sendEmails(entries);
  };

  return {
    emails,
    pagination,
    isLoading,
    isSending,
    isDismissing,
    hasEmails,
    selectedCount,
    hasSelection,
    allSelected,
    isSelected,
    toggleSelection,
    toggleSelectAll,
    clearSelection,
    fetchOutbox,
    sendEmails,
    dismissEmails,
    sendSelected,
    setEmails,
    setSelectedIds,
  };
};
